/** List-price ("Current") counterpart to the live, exec-priced solution.
 *
 *  useConfigurationLogic prices the solution as the exec has configured it —
 *  custom hardware component costs applied (calculateHardwarePricing's
 *  `customPrices`), and whatever SSD/HDD software prices and discount months
 *  are sitting in the Special Pricing card's inputs. So once an exec touches
 *  any of those, there is no list-price figure left anywhere in the UI: the
 *  card's labels read as list price while its numbers are concessions.
 *
 *  This recomputes the same configuration at list, so the card can show
 *  Current beside Custom. "At list" means all three exec levers returned to
 *  their defaults:
 *
 *    - hardware component costs      -> catalog (no customPrices)
 *    - SSD / HDD software prices     -> the VDURACare SKU price for the quote's
 *                                       quarter and service option
 *    - discount months               -> 0
 *
 *  Everything else — capacities, node counts, subscription months,
 *  registration — is held identical on both sides, so the comparison isolates
 *  what the exec chose to give away and nothing else.
 *
 *  Returns null when there is nothing to compare: no lever moved, or levers
 *  that net to identical totals. The card then renders exactly as it did
 *  before this existed. */
import { createPricingStructure, deriveCostEfficiency } from './solutionPricing';
import { calculateSoftwarePricing, calculateHardwarePricing } from './pricingCalculator';
import { getInitialSoftwarePricing } from './configUtilities';
import { createPriceResolver } from '../pricing/priceResolver';

/** The config as it would be at list price. Only the exec levers move. */
const atListPrice = (config, productDb, vendor) => {
  const defaults = getInitialSoftwarePricing(
    productDb, config.quarter, config.serviceOption, vendor, config.subscriptionMonths,
  );

  // Mirrors the adjustment the card's own input handlers apply (App.js
  // handleSsdSoftwareChange): the catalog price is a BASE price, and an
  // unregistered quote pays it as-is while a registered one pays it times the
  // vendor's multiplier. Applying the same rule to both sides keeps a
  // registration toggle from reading as an exec concession.
  const regMultiplier = vendor?.businessRules?.registrationMultiplier ?? 1;
  const asCharged = (base) =>
    (config.isNoReg ? Number(base.toFixed(2)) : Number((base * regMultiplier).toFixed(2)));

  // Two writers produce the price a registered quote is charged, and they do not
  // round alike: the card's input handlers round to the cent, rule R5 (the
  // Registered toggle) and R4 (quarter / service change) write base × multiplier
  // as-is, so 19 × 1.4 arrives as 26.599999999999998. A price that IS the list
  // price to the cent is therefore carried over exactly as the live side holds
  // it. That keeps the inputs to the two pricing passes bit-identical — which
  // the float-equality test below depends on — so the toggle cannot surface
  // float dust as a concession.
  const listPrice = (live, base) => {
    const charged = asCharged(base);
    return Math.abs(Number(live) - charged) < 0.005 ? live : charged;
  };

  return {
    ...config,
    ssdSoftware: listPrice(config.ssdSoftware, defaults.ssdSoftware),
    hddSoftware: listPrice(config.hddSoftware, defaults.hddSoftware),
    discountMonths: 0,
  };
};

export const computeBaselinePricing = ({
  config, productDb, metrics, vendor, livePricing, customPrices,
}) => {
  if (!livePricing) return null;

  const listConfig = atListPrice(config, productDb, vendor);

  // A software-subscription-only quote's BOM carries no hardware rows — the
  // partner supplies the hardware and it is never itemised — so a hardware
  // override is not part of what this quote charges. Hold customPrices
  // identical on both sides there, so they cannot manufacture a difference out
  // of hardware the customer never sees. Only the software levers can move it.
  const softwareOnly = vendor?.businessRules?.softwareSubscriptionOnly === true;

  // Was `undefined`, relying on a `= {}` default to mean "price this side at
  // catalog". That default is gone precisely because it also absorbed forgotten
  // arguments (SP2 spec §3), so the intent is now explicit.
  const baselineResolver = softwareOnly
    ? createPriceResolver(productDb, listConfig.quarter, customPrices ?? {})
    : createPriceResolver(productDb, listConfig.quarter, {});

  // Cheap pre-check so the common path — nothing overridden, nothing discounted
  // — never pays for a second pricing pass.
  const hardwareDeviates = !softwareOnly && customPrices && Object.keys(customPrices).length > 0;
  const softwareDeviates = listConfig.ssdSoftware !== config.ssdSoftware
    || listConfig.hddSoftware !== config.hddSoftware
    || (config.discountMonths || 0) !== 0;
  if (!hardwareDeviates && !softwareDeviates) return null;

  // createPricingStructure() takes no arguments and returns a fresh object
  // literal carrying its own methods, so this cannot alias the live structure.
  // Both calculators mutate ONLY the structure handed to them — config,
  // productDb and metrics are read-only inputs — so this second pass has no
  // effect on the live one. `metrics` is reused deliberately: the levers move
  // prices, never capacities, so the live metrics are the correct ones for
  // both sides.
  const pricingData = createPricingStructure();
  calculateSoftwarePricing(listConfig, productDb, metrics, pricingData, vendor);
  calculateHardwarePricing(listConfig, productDb, metrics, pricingData, baselineResolver, vendor);

  // Float equality is the correct test here: identical arithmetic over
  // identical inputs is bit-identical, so a difference is always a real
  // concession, and an exec who re-enters the exact list price correctly
  // produces no comparison at all.
  const base = pricingData.solution;
  const live = livePricing.solution;
  if (base.grandTotal === live.grandTotal
    && base.hardwareTotal === live.hardwareTotal
    && base.softwareTotal === live.softwareTotal) {
    return null;
  }

  return {
    pricingData,
    efficiency: deriveCostEfficiency(base.grandTotal, metrics, config.compressionRatio),
  };
};
